import FileSaver from 'file-saver';
import { getFromCache, checkStatus } from '@util/cache';
import { executeFetch, retryFetch, buildUrl } from '@util/fetch';
import { getHeaders } from '@util/auth-token';

const defaultErrorHandler = error => {
  throw error;
};

/**
 * getAppend - Takes filter argument and optionally returns it or applies JSON.stringify
 *
 * @param {string|Object} filter Filter to apply
 *
 * @return {string} String we want to append to url
 */
export const getAppend = filter => {
  if (!filter) {
    return '';
  }

  if (typeof filter === 'string') {
    return filter;
  }

  return JSON.stringify(filter);
};

const get = async function get(url, filter, bypassCache, options = {}) {
  try {
    const headers = await getHeaders();
    const key = `${buildUrl(url)}${getAppend(filter)}`;
    return await getFromCache(
      key,
      () =>
        retryFetch(key, {
          credentials: 'include',
          headers,
          ...options
        }),
      bypassCache
    );
  } catch (err) {
    this.onError(err);
  }
};

const update = async function update(url, data) {
  const headers = await getHeaders({
    'Content-Type': 'application/json'
  });

  const init = {
    method: 'PUT',
    headers,
    body: JSON.stringify({ data }),
    credentials: 'include'
  };
  try {
    return await executeFetch(url, init);
  } catch (err) {
    this.onError(err);
  }
};

const create = async function create(url, data) {
  try {
    const headers = await getHeaders({
      'Content-Type': 'application/json'
    });

    const init = {
      method: 'POST',
      headers,
      body: JSON.stringify({ data }),
      credentials: 'same-origin'
    };

    return await executeFetch(url, init);
  } catch (err) {
    this.onError(err);
  }
};

const del = async function del(url, data) {
  try {
    const headers = await getHeaders({
      'Content-Type': 'application/json'
    });

    const init = {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ data }),
      credentials: 'same-origin'
    };

    return await executeFetch(url, init);
  } catch (err) {
    this.onError(err);
  }
};

const createFile = async function createFile(url, file, param = 'file') {
  const formData = new FormData();
  formData.append(param, file);

  try {
    const headers = await getHeaders();
    const init = {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers
    };

    return await executeFetch(url, init);
  } catch (err) {
    this.onError(err);
  }
};

const createFiles = async function createFiles(url, files) {
  const formData = new FormData();
  files.forEach((file, index) => formData.append(`file${index}`, file));

  try {
    const headers = await getHeaders();
    const init = {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers
    };

    return await executeFetch(url, init);
  } catch (err) {
    this.onError(err);
  }
};

const download = async function download(url) {
  const key = buildUrl(url);
  const headers = await getHeaders();

  return fetch(key, {
    credentials: 'include',
    headers
  })
    .catch(this.onError)
    .then(checkStatus)
    .then(result => {
      const contentDisposition = result.headers.get('Content-Disposition') || '';

      const filename = (
        contentDisposition
          .split(';')
          .map(s => s.trim())
          .find(s => s.startsWith('filename')) || ''
      )
        .substr(9)
        .replace(/^["']|["']$/g, ''); // Remove quotes from filename if they exist

      result.blob().then(blob => FileSaver.saveAs(blob, filename));
    });
};

// Special ❄️
const currentUser = async function currentUser() {
  try {
    const headers = await getHeaders();
    const key = '/eduphoria_webcontrols/API/Status.aspx';
    const response = await retryFetch(buildUrl(key), {
      credentials: 'include',
      headers
    });
    await checkStatus(response);
    const data = await response.json();
    if (data.isAuthenticated) {
      return data;
    }

    throw Error('Not Authenticated');
  } catch (error) {
    this.onError(error);
  }
};

export default () => {
  const result = {
    get,
    update,
    create,
    createFile,
    createFiles,
    del,
    currentUser,
    download,
    onError: defaultErrorHandler
  };

  // Bind all the methods to 'result'
  const methodNames = Object.keys(result);
  methodNames.forEach(methodName => {
    const method = result[methodName];
    result[methodName] = method.bind(result);
  });

  // also return a raw version of "get" to allow for case-by-case
  // overrides of this.onError, for example
  return { ...result, rawGet: get };
};
